/**
 * Browser-side client for Emerge's modern authorization interaction endpoints.
 *
 * These calls intentionally go browser -> Emerge, matching the legacy forms,
 * so Turnstile verification and Emerge rate-limit discriminators see the real
 * client IP. This module must never log raw interaction, resume, or magic-link
 * tokens.
 */

import Bugsnag from '@bugsnag/js';
import { safeFunnelAttribution, type FunnelAttribution } from './funnel-analytics';

const API_HOST = (process.env.NEXT_PUBLIC_API_HOST || '').replace(/\/+$/, '');
const PENZU_WEB_CLIENT = 'penzu_web';

export type InteractionStatus = 'pending' | 'approved' | 'expired';

export interface ApprovedInteraction {
  status: 'approved';
  resumeUrl: string;
}

export interface MagicLinkApproval {
  status: 'approved';
  resumeUrl?: string;
}

export interface PendingInteraction {
  status: 'pending';
}

export type InteractionStatusResponse =
  | PendingInteraction
  | MagicLinkApproval
  | { status: 'expired' };

export interface InteractionLoginParams {
  interactionToken: string;
  email: string;
  password: string;
  turnstile: string;
}

export interface InteractionSignupParams {
  attribution?: FunnelAttribution;
  interactionToken: string;
  email: string;
  password: string;
  fullName: string;
  receiveMarketingEmails: boolean;
  turnstile: string;
}

export interface MagicLinkRequestParams {
  interactionToken: string;
  email: string;
  turnstile: string;
}

export interface MagicLinkConsumeParams {
  interactionToken: string;
  magicLinkToken: string;
  sameDevice: boolean;
}

export type InteractionErrorKind =
  | 'display'
  | 'invalid'
  | 'expired'
  | 'unavailable'
  | 'rate_limited'
  | 'network'
  | 'unexpected';

export class InteractionApiError extends Error {
  readonly kind: InteractionErrorKind;
  readonly status?: number;
  readonly code?: string;
  readonly title: string;
  readonly userMessage: string;

  /**
   * `Error.message` intentionally carries only the coarse title. The
   * user-facing details live in `userMessage`, so reports/logs stay readable
   * without accidentally including upstream response text that could drift.
   */
  constructor({
    kind,
    status,
    code,
    title,
    userMessage,
  }: {
    kind: InteractionErrorKind;
    status?: number;
    code?: string;
    title: string;
    userMessage: string;
  }) {
    super(title);
    this.name = 'InteractionApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.title = title;
    this.userMessage = userMessage;
  }
}

interface InteractionReportContext {
  source: string;
  endpoint?: string;
  operation?: string;
  completionMode?: string;
  originalErrorClass?: string;
}

interface EmergeErrorBody {
  error?: string | { title?: unknown; message?: unknown };
}

interface RawApproval {
  status?: unknown;
  resume_url?: unknown;
}

interface RawStatus {
  status?: unknown;
  resume_url?: unknown;
}

function interactionUrl(path: string): string {
  if (!API_HOST) {
    throw new InteractionApiError({
      kind: 'unavailable',
      code: 'missing_api_host',
      title: 'Authentication is not configured',
      userMessage: 'Please try again later.',
    });
  }
  return `${API_HOST}${path}`;
}

function parseErrorBody(body: unknown): { code?: string; title?: string; message?: string } {
  const error = (body as EmergeErrorBody | null)?.error;
  if (typeof error === 'string') {
    return { code: error };
  }
  if (error && typeof error === 'object') {
    return {
      title: typeof error.title === 'string' ? error.title : undefined,
      message: typeof error.message === 'string' ? error.message : undefined,
    };
  }
  return {};
}

async function readJsonSafely(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function isTemporarilyUnavailable(error: { code?: string; title?: string; message?: string }) {
  const text = `${error.code || ''} ${error.title || ''} ${error.message || ''}`.toLowerCase();
  return text.includes('temporarily_unavailable') || text.includes('temporarily unavailable');
}

function displayErrorForCode(code: string | undefined): { title: string; userMessage: string } {
  if (code === 'invalid_turnstile') {
    return {
      title: 'Verification failed',
      userMessage: 'Please complete the CAPTCHA and try again.',
    };
  }

  return {
    title: 'Authentication failed',
    userMessage: 'Please check your information and try again.',
  };
}

export function isTerminalInteractionError(error: InteractionApiError): boolean {
  return error.kind === 'invalid' || error.kind === 'expired' || error.kind === 'unavailable';
}

export function isRetryableInteractionError(error: InteractionApiError): boolean {
  return error.kind === 'network' || error.kind === 'rate_limited' || error.kind === 'unexpected';
}

function shouldReportInteractionError(error: InteractionApiError): boolean {
  return (
    error.kind === 'network' ||
    error.kind === 'unexpected' ||
    error.code === 'missing_api_host'
  );
}

export function reportInteractionFailure(error: unknown, context: InteractionReportContext): void {
  const interactionError = error instanceof InteractionApiError ? error : null;
  if (interactionError && !shouldReportInteractionError(interactionError)) {
    return;
  }

  // Do not forward an arbitrary Error instance: browser/network error messages
  // can include a URL with secret-bearing query parameters. Metadata below is
  // deliberately limited to the safe normalized fields.
  const reportableError = new Error('Modern auth interaction failure');
  const metadata = {
    source: context.source,
    endpoint: context.endpoint,
    operation: context.operation,
    completionMode: context.completionMode,
    originalErrorClass: context.originalErrorClass,
    kind: interactionError?.kind,
    status: interactionError?.status,
    code: interactionError?.code,
  };

  if (typeof window !== 'undefined' && Bugsnag.isStarted()) {
    Bugsnag.notify(reportableError, (event) => {
      event.severity = interactionError?.kind === 'network' ? 'warning' : 'error';
      event.addMetadata('authInteraction', metadata);
    });
  }

  console.error('auth.interaction failure', metadata, reportableError.name);
}

function errorFromResponse(response: Response, body: unknown): InteractionApiError {
  const parsedError = parseErrorBody(body);

  if (response.status === 422) {
    const displayError = displayErrorForCode(parsedError.code);
    return new InteractionApiError({
      kind: 'display',
      status: response.status,
      code: parsedError.code,
      title: parsedError.title || displayError.title,
      userMessage: parsedError.message || displayError.userMessage,
    });
  }

  if (response.status === 404 && isTemporarilyUnavailable(parsedError)) {
    return new InteractionApiError({
      kind: 'unavailable',
      status: response.status,
      code: parsedError.code,
      title: 'Authentication is temporarily unavailable',
      userMessage: 'Please wait a few minutes, then try again.',
    });
  }

  if (response.status === 404) {
    return new InteractionApiError({
      kind: 'invalid',
      status: response.status,
      code: parsedError.code,
      title: 'This sign-in link is no longer valid',
      userMessage: 'Start over to continue signing in.',
    });
  }

  if (response.status === 410) {
    return new InteractionApiError({
      kind: 'expired',
      status: response.status,
      code: parsedError.code,
      title: 'This sign-in request has expired',
      userMessage: 'Start over to continue signing in.',
    });
  }

  if (response.status === 429) {
    return new InteractionApiError({
      kind: 'rate_limited',
      status: response.status,
      code: parsedError.code,
      title: 'Too many attempts',
      userMessage: 'Please wait a few minutes before trying again.',
    });
  }

  return new InteractionApiError({
    kind: 'unexpected',
    status: response.status,
    code: parsedError.code,
    title: 'Authentication failed',
    userMessage: 'Please try again.',
  });
}

async function interactionFetch(
  path: string,
  init: RequestInit = {},
  endpoint: string = path,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(interactionUrl(path), {
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'omit',
      redirect: 'follow',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (error) {
    if (error instanceof InteractionApiError) {
      reportInteractionFailure(error, {
        source: 'auth-interactions-api',
        endpoint,
        operation: 'configure',
      });
      throw error;
    }

    const apiError = new InteractionApiError({
      kind: 'network',
      title: 'Authentication is unavailable',
      userMessage: 'Check your connection and try again.',
    });
    reportInteractionFailure(apiError, {
      source: 'auth-interactions-api',
      endpoint,
      operation: 'fetch',
      originalErrorClass: error instanceof Error ? error.name : typeof error,
    });
    throw apiError;
  }

  const body = await readJsonSafely(response);
  if (!response.ok) {
    const apiError = errorFromResponse(response, body);
    reportInteractionFailure(apiError, {
      source: 'auth-interactions-api',
      endpoint,
      operation: 'response',
    });
    throw apiError;
  }
  return body;
}

function unexpectedResponseError(
  endpoint: string,
  title: string,
  userMessage: string = 'Please start over and try again.',
): InteractionApiError {
  const error = new InteractionApiError({
    kind: 'unexpected',
    title,
    userMessage,
  });
  reportInteractionFailure(error, {
    source: 'auth-interactions-api',
    endpoint,
    operation: 'parse',
  });
  return error;
}

function approvedFromBody(
  body: unknown,
  fallbackTitle: string,
  endpoint: string,
): ApprovedInteraction {
  const result = body as RawApproval | null;
  if (result?.status === 'approved' && typeof result.resume_url === 'string') {
    return { status: 'approved', resumeUrl: result.resume_url };
  }
  throw unexpectedResponseError(endpoint, fallbackTitle);
}

export async function loginInteraction(params: InteractionLoginParams): Promise<ApprovedInteraction> {
  const endpoint = '/api/auth/interactions/login';
  const body = await interactionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      interaction_token: params.interactionToken,
      email: params.email,
      password: params.password,
      client: PENZU_WEB_CLIENT,
      turnstile: params.turnstile,
    }),
  });

  return approvedFromBody(body, 'Login failed', endpoint);
}

export async function signupInteraction(
  params: InteractionSignupParams,
): Promise<ApprovedInteraction> {
  const endpoint = '/api/auth/interactions/signup';
  const body = await interactionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      interaction_token: params.interactionToken,
      email: params.email,
      password: params.password,
      full_name: params.fullName,
      receive_marketing_emails: params.receiveMarketingEmails,
      ...safeFunnelAttribution(params.attribution),
      client: PENZU_WEB_CLIENT,
      turnstile: params.turnstile,
    }),
  });

  return approvedFromBody(body, 'Signup failed', endpoint);
}

export async function requestMagicLink(
  params: MagicLinkRequestParams,
): Promise<PendingInteraction> {
  const endpoint = '/api/auth/interactions/magic_link/request';
  const body = (await interactionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      interaction_token: params.interactionToken,
      email: params.email,
      turnstile: params.turnstile,
    }),
  })) as RawStatus | null;

  if (body?.status === 'pending') {
    return { status: 'pending' };
  }

  throw unexpectedResponseError(endpoint, 'Magic link request failed', 'Please try again.');
}

export async function consumeMagicLink(
  params: MagicLinkConsumeParams,
): Promise<MagicLinkApproval> {
  const endpoint = '/api/auth/interactions/magic_link/consume';
  const body = (await interactionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      interaction_token: params.interactionToken,
      magic_link_token: params.magicLinkToken,
      same_device: params.sameDevice,
    }),
  })) as RawApproval | null;

  if (body?.status === 'approved') {
    return {
      status: 'approved',
      ...(typeof body.resume_url === 'string' ? { resumeUrl: body.resume_url } : {}),
    };
  }

  throw unexpectedResponseError(endpoint, 'Magic link failed');
}

export async function fetchInteractionStatus(
  interactionToken: string,
): Promise<InteractionStatusResponse> {
  const endpoint = '/api/auth/interactions/:interaction_token';
  const body = (await interactionFetch(
    `/api/auth/interactions/${encodeURIComponent(interactionToken)}`,
    { method: 'GET' },
    endpoint,
  )) as RawStatus | null;

  if (body?.status === 'pending' || body?.status === 'approved' || body?.status === 'expired') {
    return {
      status: body.status,
      ...(typeof body.resume_url === 'string' ? { resumeUrl: body.resume_url } : {}),
    };
  }

  throw unexpectedResponseError(endpoint, 'Authentication status is unavailable');
}
